import { useCallback, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useFormContext } from '@/components/react-hook-form';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { ManageIntegrationDataPane } from '@/modules/household/ManageIntegrationDataModal/ManageIntegrationDataModal.types';
import {
  TROWSER_PARAM,
  TROWSER_SECTION_PARAM,
  TrowserKey,
} from '@/modules/navigation/trowsers/trowser.types';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

import { ClientSetupFormShape } from '../../clientSetup/ClientSetup.type';
import { useDocumentUploadStepContext } from '../context/documentUploadStep.context';
import { usePersistUploadedDocuments } from './usePersistUploadedDocuments';

export interface DocumentUploadExitActions {
  /**
   * Persist any uploaded documents, close the wizard, and open the "Link data"
   * modal at the "Link entities" step for the new household. Only meaningful
   * when `canCreateBalanceSheet` is true.
   */
  createBalanceSheetFromIntegration: () => Promise<void>;
  /** Whether a data-link integration was selected in step 1. */
  canCreateBalanceSheet: boolean;
  /** True while an exit action is persisting/navigating. */
  isExiting: boolean;
}

/** Persists uploads before leaving onboarding for the integration flow. */
export function useDocumentUploadExitActions(): DocumentUploadExitActions {
  const { householdId, closeWizard, hasBeenClosed, markCommitted } =
    useDocumentUploadStepContext();
  const { persist } = usePersistUploadedDocuments();
  const { whenUploadsSettle } = useMultiDocumentUploaderContext();
  const navigate = useNavigate();
  const { control } = useFormContext<ClientSetupFormShape>();

  const [isExiting, setIsExiting] = useState(false);

  // `persist` closes over the current uploaded-file set. Since `exitTo` awaits
  // in-flight uploads before persisting, the set can grow during that await, so
  // we call the latest `persist` (via a ref) rather than the one captured when
  // the action fired — otherwise a just-landed file would be missed.
  const persistRef = useRef(persist);
  persistRef.current = persist;

  const integrationClientIds = useWatch({
    control,
    name: 'integrationClientIds',
  });
  const canCreateBalanceSheet = (integrationClientIds ?? []).length > 0;

  const exitTo = useCallback(
    async (navigateToDestination: () => void) => {
      if (!householdId) return;
      markCommitted();
      setIsExiting(true);
      try {
        // These actions leave the wizard, so uploads can't finish in the
        // background — wait for any in-flight uploads to land, then persist,
        // before navigating away.
        await whenUploadsSettle();
        const didPersist = await persistRef.current();
        // On a save error stay in the flow (the error is surfaced); don't
        // navigate. Also bail if the modal was closed while the save was in
        // flight, so we don't close/navigate away from a reset session.
        if (!didPersist || hasBeenClosed()) return;
        closeWizard();
        navigateToDestination();
      } finally {
        setIsExiting(false);
      }
    },
    [householdId, markCommitted, whenUploadsSettle, closeWizard, hasBeenClosed]
  );

  const createBalanceSheetFromIntegration = useCallback(
    () =>
      exitTo(() => {
        if (!householdId) return;
        const path = getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS,
          { householdId },
          {
            [TROWSER_PARAM]: TrowserKey.LinkHouseholdData,
            [TROWSER_SECTION_PARAM]: ManageIntegrationDataPane.LinkTrusts,
          }
        );
        navigate(path, { replace: true });
      }),
    [exitTo, householdId, navigate]
  );

  return {
    createBalanceSheetFromIntegration,
    canCreateBalanceSheet,
    isExiting,
  };
}
